/**
 * Tank listesi — sahadakiler VE depodakiler.
 *
 * Tankın iki hâli var (`ck_site_tank`): sahada kurulu ya da depoda. İkisini
 * ayrı ekranlara bölmedik — envanterin tamamı tek yerde görünmeli, yoksa
 * "elimizde kaç tank var" sorusu iki ekranı toplamayı gerektirir. Filtre
 * ADRESTE: `?depoda=1` paylaşılabilir bir görünümdür.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import {
  AramaKutusu,
  EklemeBaglantisi,
  FiltreDugmesi,
  FiltreSatiri,
  KapsamNotu,
  ListeDurumu,
  YapilandirmaSekmeleri,
  useAramaYazici,
} from './parts.tsx';
import { Badge } from '../design/Status.tsx';
import { Yetki } from '../auth/Yetki.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { tankYeri, yonMetni } from './durum.ts';
import { useTankKapasiteleri, useTanklar, type TankSatiri } from './api.ts';

export function TankListesi(): React.JSX.Element {
  const { q, depoda, kapasite } = useSearch({ from: '/korumali/tanklar' });
  const kapasiteler = useTankKapasiteleri();
  const navigate = useNavigate();

  const git = useCallback(
    (yeni: {
      q?: string | undefined;
      depoda?: true | undefined;
      kapasite?: number | undefined;
    }) => {
      void navigate({
        to: '/tanklar',
        search: {
          ...(yeni.q === undefined ? {} : { q: yeni.q }),
          ...(yeni.depoda === undefined ? {} : { depoda: true as const }),
          ...(yeni.kapasite === undefined ? {} : { kapasite: yeni.kapasite }),
        },
        replace: true,
      });
    },
    [navigate],
  );

  const yaz = useCallback((deger: string | undefined) => git({ q: deger, depoda }), [git, depoda]);
  const aramaDegisti = useAramaYazici(yaz);
  const temizle = useCallback(() => git({}), [git]);

  const sorgu = useTanklar({ q, depoda, kapasite });
  const filtreVar = q !== undefined || depoda === true || kapasite !== undefined;

  return (
    <>
      <PageHeader
        title="Tanklar"
        description="Sahalardaki ve depodaki tank envanteri"
        action={
          <EklemeBaglantisi gerekli="add_tank" to="/tanklar/yeni" etiket="Tank ekle" vurgulu />
        }
      />
      <YapilandirmaSekmeleri />
      <KapsamNotu />
      <AramaKutusu
        deger={q ?? ''}
        degisti={aramaDegisti}
        ipucu="Seri numarası, saha adı veya marka"
      />

      <FiltreSatiri>
        <FiltreDugmesi
          etkin={depoda === true}
          onClick={() => git({ q, kapasite, depoda: depoda === true ? undefined : true })}
        >
          Yalnız depodakiler
        </FiltreDugmesi>
        {/*
         * Kapasite sınıfları LİSTEDEN geliyor (S13'ün sebebi): serbest sayı
         * yerine tabloda gerçekten var olan sınıflar.
         */}
        {(kapasiteler.data ?? []).map((k) => (
          <FiltreDugmesi
            key={k.capacity_m3}
            etkin={kapasite === k.capacity_m3}
            onClick={() =>
              git({
                q,
                depoda,
                kapasite: kapasite === k.capacity_m3 ? undefined : k.capacity_m3,
              })
            }
          >
            {k.capacity_m3} m³
          </FiltreDugmesi>
        ))}
      </FiltreSatiri>

      <PageBody>
        <ListeDurumu
          sorgu={sorgu}
          filtreVar={filtreVar}
          filtreyiTemizle={temizle}
          bosBaslik="Henüz tank yok"
          bosAciklama="Tanklar sahaya kurulmadan da (depoda) kaydedilebilir."
          bosEylem={
            <EklemeBaglantisi
              gerekli="add_tank"
              to="/tanklar/yeni"
              etiket="İlk tankı ekle"
              vurgulu
            />
          }
        >
          {(tanklar) => (
            <ul className="flex flex-col gap-3">
              {tanklar.map((tank) => (
                <li key={tank.id}>
                  <TankKarti tank={tank} yerGoster />
                </li>
              ))}
            </ul>
          )}
        </ListeDurumu>
      </PageBody>
    </>
  );
}

type TankKartVerisi = Omit<TankSatiri, 'site_id' | 'site_name' | 'nominal_sm3'> &
  Partial<Pick<TankSatiri, 'site_id' | 'site_name'>>;

/** Saha detayında da kullanılıyor; orada yer bilgisi başlıkta olduğu için kapalı. */
export function TankKarti({
  tank,
  yerGoster = false,
}: {
  readonly tank: TankKartVerisi;
  readonly yerGoster?: boolean;
}): React.JSX.Element {
  const yer = tankYeri({
    site_id: tank.site_id ?? null,
    site_name: tank.site_name ?? null,
    tank_number: tank.tank_number,
  });
  const yon = yonMetni(tank.orientation);

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-ink">{tank.serial_number}</p>
            <p className="mt-0.5 truncate text-sm text-ink-muted">
              {tank.capacity_m3} m³
              {/*
               * Nominal kg kapasiteden TÜRETİLİR (`tank_capacities`) ve
               * kullanıcının günlük konuştuğu birim odur. Eski sistemde
               * tabloda karşılığı olmayan 20 m³'lük bir tank vardı ve kg
               * hesabı onun için sessizce çalışmıyordu (S13) — burada
               * karşılığı yoksa AÇIKÇA yazıyoruz.
               */}
              {tank.nominal_kg === null ? ' · kg karşılığı tanımsız' : ` · ${tank.nominal_kg} kg`}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            {yerGoster && <Badge tone={yer.tone}>{yer.metin}</Badge>}
            <Yetki gerekli="edit_tank">
              <Link
                to="/tanklar/$tankId/duzenle"
                params={{ tankId: String(tank.id) }}
                className="text-sm font-medium text-brand-700"
              >
                Düzenle
              </Link>
            </Yetki>
          </div>
        </div>

        {(tank.brand !== null || tank.production_year !== null || yon !== null) && (
          <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
            {tank.brand !== null && (
              <div className="flex min-w-0 gap-1">
                <dt>Marka:</dt>
                <dd className="truncate font-medium text-ink">{tank.brand}</dd>
              </div>
            )}
            {tank.production_year !== null && (
              <div className="flex gap-1">
                <dt>Üretim:</dt>
                <dd className="font-medium text-ink">{tank.production_year}</dd>
              </div>
            )}
            {yon !== null && (
              <div className="flex gap-1">
                <dt>Konum:</dt>
                <dd className="font-medium text-ink">{yon}</dd>
              </div>
            )}
          </dl>
        )}
      </CardBody>
    </Card>
  );
}
